import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { message } from "antd";
import { EventApplicationService } from "../../services/eventApplicationService";
import {
	ApiResponse,
	EventParticipantItem,
	EventApplicationItem,
	EventParticipantFilter,
	OperationStatus,
} from "./constants";
import { getBlobErrorMessage, getErrorMessage } from "../../utils/errorUtils";
import { downloadBlob } from "../../utils/downloadFile";

interface EventApplicationContextState {
	// Core Domain Data
	participantList: EventParticipantItem[];
    applicationList: EventApplicationItem[];
	operationStatus: OperationStatus;

	// Setters
	clearParticipantList: () => void;
    clearApplicationList: () => void;

	// Application Actions
	getEventParticipantList: (eventId: string) => Promise<void>;
	getEventApplicationList: () => Promise<void>;
    exportEventParticipantExcel: (eventId: string, filter: EventParticipantFilter) => Promise<void>;
    exportEventApplicationsExcel: () => Promise<void>;
}

interface EventApplicationProviderProps {
	children: React.ReactNode
}

const EventApplicationContext = createContext<EventApplicationContextState | undefined>(undefined);

export const useEventApplicationContext = () => {
	const context = useContext(EventApplicationContext);
	if (!context) {
		throw new Error("useEventApplicationContext must be used within an EventApplicationProvider");
	}
	return context;
};

const getResponseErrorMessage = (response: ApiResponse<unknown>) => {
	const error = response.error;
	if (typeof error === "string") {
		return error;
	}
	return error?.message || "Beklenmedik bir hata oluştu.";
};

export const EventApplicationContextProvider: React.FC<EventApplicationProviderProps> = ({ children }) => {
	const [participantList, setParticipantList] = useState<EventParticipantItem[]>([]);
	const [applicationList, setApplicationList] = useState<EventApplicationItem[]>([]);
	const [operationStatus, setOperationStatus] = useState<OperationStatus>({ type: "idle" });

	const handleError = (error: unknown, operation: string) => {
		console.error(error);
		const errMsg = getErrorMessage(error);
		setOperationStatus({ type: "error", operation, message: errMsg });
		message.error(errMsg);
	};

	const handleExportError = async (error: unknown) => {
		console.error(error);
		const errMsg = await getBlobErrorMessage(error);
		setOperationStatus({ type: "error", operation: "export", message: errMsg });
		message.error(errMsg);
	};

	/* -------------- Clear helpers -------------- */

	const clearParticipantList = useCallback(() => {
		setParticipantList([]);
	}, []);

	const clearApplicationList = useCallback(() => {
		setApplicationList([]);
	}, []);

	const getEventApplicationList = useCallback(async () => {
		setOperationStatus({ type: "loading", operation: "fetch" });

		try {
			const response = await EventApplicationService.getEventApplicationList();

			if (response.isSuccessful) {
				setApplicationList(response.result?.items ?? []);
			} else {
				message.error(getResponseErrorMessage(response));
			}
		} catch (error) {
			handleError(error, "fetch");
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, []);


	const getEventParticipantList = useCallback(async (eventId: string) => {
		setOperationStatus({ type: "loading", operation: "fetch" });

		try {
			const response = await EventApplicationService.getEventParticipantList(eventId);

			if (response.isSuccessful) {
				setParticipantList(response.result?.items ?? []);
			} else {
				message.error(getResponseErrorMessage(response));
			}
		} catch (error) {
			handleError(error, "fetch");
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, []);

	const exportEventParticipantExcel = useCallback(async (eventId: string, filter: EventParticipantFilter) => {
		setOperationStatus({ type: "loading", operation: "export" });

		try {
			const excelData = await EventApplicationService.exportEventParticipantExcel(eventId, filter);
			downloadBlob(excelData.blob, excelData.fileName);
		} catch (error) {
			await handleExportError(error);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, []);

	const exportEventApplicationsExcel = useCallback(async () => {
		setOperationStatus({ type: "loading", operation: "export" });

		try {
			const excelData = await EventApplicationService.exportEventApplicationsExcel();
			downloadBlob(excelData.blob, excelData.fileName);
		} catch (error) {
			await handleExportError(error);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, []);

	const contextValue = useMemo<EventApplicationContextState>(
		() => ({
			participantList,
			applicationList,
			operationStatus,
			clearParticipantList,
			clearApplicationList,
			getEventParticipantList,
			getEventApplicationList,
			exportEventParticipantExcel,
			exportEventApplicationsExcel,
		}),
		[
			participantList,
			applicationList,
			operationStatus,
			clearParticipantList,
			clearApplicationList,
			getEventParticipantList,
			getEventApplicationList,
			exportEventParticipantExcel,
			exportEventApplicationsExcel,
		]
	);

	return <EventApplicationContext.Provider value={contextValue}>{children}</EventApplicationContext.Provider>;
};